import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MapConfiguration, Pin } from '@shared/ahoy-maps/ahoy-maps.service';

@Component({
  selector: 'ahoy-maps',
  templateUrl: './ahoy-maps.component.html',
  styleUrls: ['./ahoy-maps.component.scss'],
  standalone: false,
})
export class AhoyMapsComponent implements OnInit, AfterViewInit {
  @ViewChild('mapContainer') mapContainer!: ElementRef;
  @Input() configuration: MapConfiguration | undefined;
  @Input() pins: Pin[] = [];

  platform: H.service.Platform;
  map: H.Map;
  behavior: H.mapevents.Behavior;
  markers: H.map.Marker[] = [];

  private routeLine: H.map.Polyline | null = null;

  constructor() {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pins?.currentValue && !changes.pins.firstChange) {
      this.setUpHereMap();
    }
  }

  ngAfterViewInit(): void {
    // setup here map
    this.setUpHereMap();
  }

  /**
   * setting up here map with satalite settings and events
   */
  setUpHereMap() {
    if (typeof H !== 'undefined') {
      this.platform = new H.service.Platform({
        apikey: this.configuration.key,
      });

      const defaultLayers = this.platform.createDefaultLayers();
      this.mapContainer.nativeElement.innerHTML = '';

      this.map = new H.Map(
        this.mapContainer.nativeElement,
        defaultLayers.vector.normal.map,
        {
          zoom: 15,
          center: {
            lat: this.configuration.lat ?? this.pins[0]?.latitude,
            lng: this.configuration.lng ?? this.pins[0]?.longitude,
          },
          padding: { top: 50, left: 50, bottom: 50, right: 50 },
        },
      );

      const mapEvents = new H.mapevents.MapEvents(this.map);
      this.behavior = new H.mapevents.Behavior(mapEvents);

      this.map.addEventListener('tap', (ev: H.HereMapEvent) => {
        const coord = this.map.screenToGeo(
          ev.currentPointer.viewportX,
          ev.currentPointer.viewportY,
        );
        if (this.pins[0]?.draggable !== false) {
          this.markers[0].setGeometry(coord);
        }
        this.setAddressByCoords(coord.lat, coord.lng);
      });

      H.ui.UI.createDefault(this.map, defaultLayers);

      const markerBounds: { lat: string; lng: string }[] = [];
      this.pins.forEach((pin) => {
        this.markers[pin.index] = this.addMarker(
          this.map,
          { lat: pin.latitude, lng: pin.longitude },
          this.behavior,
          pin.icon,
          pin.draggable,
        );
        markerBounds.push({ lat: pin.latitude, lng: pin.longitude });
      });

      this.setViewBounds(markerBounds);
    }
  }

  setViewBounds(markers: { lat: string; lng: string }[]): void {
    const group = new H.map.Group();

    if (markers?.length > 0) {
      markers.forEach((marker) => {
        const element = new H.map.Marker(marker);
        group.addObject(element);
      });

      this.map.addObject(group);
      this.map.getViewModel().setLookAtData({
        bounds: group.getBoundingBox(),
      });
      this.map.removeObject(group);

      if (markers.length === 1) {
        this.map.getViewModel().setLookAtData({
          zoom: 12,
        });
      }
    }
  }

  /**
   * Add marker on here map object in
   * the coordinates specified
   * and with the same behavior
   * @param map here map object
   * @param coords coordinates latitude & longitude
   * @param behavior here map behavior object
   */
  private addMarker(
    map: H.Map,
    coords: { lat: number | string; lng: number | string },
    behavior: H.mapevents.Behavior,
    icon: string,
    draggable: boolean,
  ) {
    const markerIcon = new H.map.Icon(icon);
    const marker = new H.map.Marker(coords, {
      volatility: true,
      icon: markerIcon,
    });

    marker.draggable = draggable;

    map.addObject(marker);
    map.setCenter(coords);

    map.addEventListener('dragstart', (ev: H.HereMapEvent) => {
      const target = ev.target;
      const pointer = ev.currentPointer;
      if (target instanceof H.map.Marker) {
        const targetPosition = map.geoToScreen(target.getGeometry());
        target.offset = new H.math.Point(
          pointer.viewportX - targetPosition.x,
          pointer.viewportY - targetPosition.y,
        );
        behavior.disable();
      }
    });

    map.addEventListener('dragend', (ev: H.HereMapEvent) => {
      const target = ev.target;
      if (target instanceof H.map.Marker) {
        behavior.enable();
        const coord = this.map.screenToGeo(
          ev.currentPointer.viewportX,
          ev.currentPointer.viewportY,
        );
        this.setAddressByCoords(coord.lat, coord.lng);
      }
    });

    map.addEventListener('drag', (ev: H.HereMapEvent) => {
      const target = ev.target;
      const pointer = ev.currentPointer;
      if (target instanceof H.map.Marker) {
        target.setGeometry(
          map.screenToGeo(
            pointer.viewportX - target.offset.x,
            pointer.viewportY - target.offset.y,
          ),
        );
      }
    });

    return marker;
  }

  /**
   * get the address string from google api
   * and set the address input to the fetched string
   * @param lat latitude number
   * @param lng longitude number
   */
  private setAddressByCoords(lat: number, lng: number) {}

  updateMarkersLocation(pins: Pin[]) {
    pins.forEach((pin) => {
      this.markers[pin.index].setGeometry({
        lat: Number(pin.latitude),
        lng: Number(pin.longitude),
      });
    });
  }

  /**
   * Parses route segments with LINESTRING and draws a route polyline
   * @param segments List of route segments
   */
  drawRouteFromSegments(segments: { geom: string }[]): void {
    if (!this.map || typeof H === 'undefined' || !segments?.length) return;

    const lineString = new H.geo.LineString();

    segments.forEach((segment) => {
      const coords = this.parseLineString(segment.geom);
      coords.forEach((coord) => {
        lineString.pushPoint(coord);
      });
    });

    // Remove old route line
    if (this.routeLine) {
      this.map.removeObject(this.routeLine);
    }

    this.routeLine = new H.map.Polyline(lineString, {
      style: {
        lineWidth: 5,
        strokeColor: '#0078D7',
        lineTailCap: 'round',
        lineHeadCap: 'round',
      },
    });

    this.map.addObject(this.routeLine);

    // Zoom to fit route
    this.map.getViewModel().setLookAtData({
      bounds: this.routeLine.getBoundingBox(),
    });
  }

  /**
   * Removes the drawn route from the map
   */
  removeRoute(): void {
    if (this.routeLine) {
      this.map.removeObject(this.routeLine);
      this.routeLine = null;
    }
  }
  /**
   * Removes all markers from the map
   */
  removeMarkers(): void {
    this.markers.forEach((marker) => {
      if (marker) {
        this.map.removeObject(marker);
      }
    });
    this.markers = [];
  }
  /**
   * Parses WKT LINESTRING into array of lat/lng
   * @param wkt "LINESTRING(lon lat, lon lat, ...)"
   * @returns Array of {lat, lng}
   */
  private parseLineString(wkt: string): { lat: number; lng: number }[] {
    const line = wkt.replace(/LINESTRING\s*\(/i, '').replace(')', '');
    const points = line.split(',').map((coord) => {
      const [lng, lat] = coord.trim().split(' ').map(Number);
      return { lat, lng };
    });
    return points;
  }
}
